import { COSMOS_URL } from 'components/globals';
import { CityLink } from 'components/marketplace/directory/CityLinkList';
import { DirectoryTemplate } from 'components/marketplace/directory/DirectoryTemplate';
import { EmptyStateNearby } from 'components/marketplace/directory/EmptyStateNearby';
import {
  getDirectoryPageData,
  getNearestStatesWithInventory,
} from 'components/marketplace/directory/ssr';
import { StateLink } from 'components/marketplace/directory/StateLinkList';
import { ListingCardData } from 'components/marketplace/property/SimilarListings';
import { ISiteFooter, ISiteHeader } from 'declarations/contentful';
// eslint-disable-next-line no-restricted-imports
import { GetServerSideProps, NextPage } from 'next';

interface StateDirectoryPageProps {
  state: string;
  stateDisplayName: string;
  listings: ListingCardData[];
  totalCount: number;
  currentPage: number;
  nextPage?: number | null;
  citiesWithInventory: CityLink[];
  statesWithInventory: StateLink[];
  nearMeCity?: string | null;
  nearMeCityCount?: number | null;
  aiBlurb?: string | null;
  isEmpty?: boolean | null;
  nearestStates?: StateLink[];
  displayingCity?: string | null;
  availableCities?: Array<{ name: string; slug: string }> | null;
  footer?: ISiteFooter | null;
  header?: ISiteHeader | null;
  marketSlug?: string | null;
}

const formatStateName = (slug: string): string => {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const StateDirectoryPage: NextPage<StateDirectoryPageProps> = (props) => {
  if (props.isEmpty && props.nearestStates) {
    return (
      <EmptyStateNearby
        stateName={props.stateDisplayName}
        nearestStates={props.nearestStates}
        allStates={props.statesWithInventory}
      />
    );
  }

  const seoTitle = `Homes for Sale in ${props.stateDisplayName} | Opendoor`;
  const seoDescription = `Browse homes for sale across ${props.stateDisplayName}. Find houses, condos, and town homes in cities throughout the state with Opendoor.`;
  const canonicalUrl = `${COSMOS_URL}/homes/state/${props.state}`;

  return (
    <DirectoryTemplate
      pageType="state"
      locationName={props.stateDisplayName}
      locationSlug={props.state}
      listings={props.listings}
      totalCount={props.totalCount}
      currentPage={props.currentPage}
      nextPage={props.nextPage}
      citiesWithInventory={props.citiesWithInventory}
      statesWithInventory={props.statesWithInventory}
      nearMeCity={props?.nearMeCity}
      nearMeCityCount={props?.nearMeCityCount}
      aiBlurb={props?.aiBlurb}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      canonicalUrl={canonicalUrl}
      displayingCity={props?.displayingCity}
      availableCities={props?.availableCities}
      footer={props?.footer}
      header={props?.header}
      marketSlug={props?.marketSlug ?? undefined}
    />
  );
};

export const getServerSideProps: GetServerSideProps<StateDirectoryPageProps> = async (context) => {
  const state = context.params?.state as string;

  if (!state) {
    return { notFound: true };
  }

  const page = parseInt((context.query.page as string) || '1', 10);
  const selectedCity = (context.query.city as string) || undefined;
  const data = await getDirectoryPageData('state', state, page, context.req.headers, selectedCity);

  if (data.totalCount === 0 && page === 1) {
    const nearestStates = await getNearestStatesWithInventory(state, context.req.headers);
    return {
      props: {
        state,
        stateDisplayName: formatStateName(state),
        listings: [],
        totalCount: 0,
        currentPage: page,
        nextPage: null,
        citiesWithInventory: data.citiesWithInventory,
        statesWithInventory: data.statesWithInventory,
        isEmpty: true,
        nearestStates,
        footer: data.footer ?? null,
        header: data.header ?? null,
      },
    };
  }

  // If no listings and it's page > 1, show 404
  if (data.totalCount === 0 && page > 1) {
    return { notFound: true };
  }

  return {
    props: {
      state,
      stateDisplayName: formatStateName(state),
      listings: data.listings,
      totalCount: data.totalCount,
      currentPage: page,
      nextPage: data.nextPage ?? null,
      citiesWithInventory: data.citiesWithInventory,
      statesWithInventory: data.statesWithInventory,
      nearMeCity: data.nearMeCity ?? null,
      nearMeCityCount: data.nearMeCityCount ?? null,
      aiBlurb: data.aiBlurb ?? null,
      displayingCity: data.displayingCity ?? null,
      availableCities: data.availableCities ?? null,
      footer: data.footer ?? null,
      header: data.header ?? null,
      marketSlug: data.marketSlug ?? null,
    },
  };
};

export default StateDirectoryPage;
